import { Check, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { multimodalApi } from '@/api/multimodalApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ZoomableImage } from '@/components/ui/zoomable-image'
import { cn } from '@/lib/utils'
import type { Asset } from '@/types/nf'

export interface PanelPoolCandidate {
  asset: Asset
  groupId: string
  score: number
  rank: number
  primary: boolean
  style: string
  seed: number | null
  replayable: boolean
}

interface PanelPoolPayload {
  panel_id?: string
  seed?: number
  replay_request?: { Seed?: number; RefImage?: string }
  metadata?: {
    pool_group_id?: string
    pool_score?: number
    pool_rank?: number
    pool_primary?: boolean
    pool_style_preset?: string
  }
}

export function panelPoolCandidatesForPanel(panelId: string, assets: Asset[]): PanelPoolCandidate[] {
  const candidates = assets
    .map(parsePanelPoolCandidate)
    .filter((candidate): candidate is PanelPoolCandidate => Boolean(candidate))
    .filter((candidate) => panelIdForAsset(candidate.asset) === panelId)

  if (candidates.length === 0) return []
  const primaryGroup = candidates.find((candidate) => candidate.primary)?.groupId
  const groupId = primaryGroup ?? latestPoolGroup(candidates)
  return candidates
    .filter((candidate) => candidate.groupId === groupId)
    .sort((a, b) => b.score - a.score || a.rank - b.rank || b.asset.updated_at.localeCompare(a.asset.updated_at))
}

export function updatePanelPoolPrimary(assets: Asset[], panelId: string, assetId: string): Asset[] {
  const selected = panelPoolCandidatesForPanel(panelId, assets).find((candidate) => candidate.asset.id === assetId)
  if (!selected) return assets
  return assets.map((asset) => {
    const candidate = parsePanelPoolCandidate(asset)
    if (!candidate || panelIdForAsset(asset) !== panelId || candidate.groupId !== selected.groupId) return asset
    return {
      ...asset,
      payload: writePoolPrimary(asset.payload, asset.id === assetId),
    }
  })
}

export function PanelPoolGallery({
  panelId,
  assets,
  selectingAssetId,
  onSelect,
  onReplay,
  replayingAssetId,
}: {
  panelId: string
  assets: Asset[]
  selectingAssetId: string | null
  onSelect: (assetId: string) => void
  onReplay?: (assetId: string) => void
  replayingAssetId?: string | null
}) {
  const { t } = useTranslation()
  const candidates = panelPoolCandidatesForPanel(panelId, assets)
  if (candidates.length === 0) return null

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold tracking-normal">{t('detail.pipelineViewer.poolGallery.title')}</h4>
        <Badge variant="secondary" className="rounded-md">
          {t('detail.pipelineViewer.poolGallery.count', { count: candidates.length })}
        </Badge>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {candidates.map((candidate) => {
          const selecting = selectingAssetId === candidate.asset.id
          return (
            <div key={candidate.asset.id} className="space-y-2">
            <button
              type="button"
              className={cn(
                'w-full overflow-hidden rounded-md border bg-background text-left transition hover:border-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                candidate.primary ? 'border-primary shadow-sm ring-1 ring-primary/40' : 'border-border',
              )}
              disabled={candidate.primary || Boolean(selectingAssetId)}
              onClick={() => onSelect(candidate.asset.id)}
              aria-label={t('detail.pipelineViewer.poolGallery.selectCandidate', { rank: candidate.rank })}
            >
              <ZoomableImage
                src={multimodalApi.panelPoolAssetImageUrl(panelId, candidate.asset.id)}
                alt={t('detail.pipelineViewer.poolGallery.candidateAlt', { rank: candidate.rank })}
                zoomLabel={t('detail.pipelineViewer.poolGallery.zoom')}
                stopPropagation
              />
              <div className="space-y-2 p-2">
                {candidate.style ? <Badge variant="outline">{t(`detail.pipelineViewer.poolGallery.styles.${candidate.style}`, { defaultValue: candidate.style })}</Badge> : null}
                {candidate.seed !== null ? <p className="break-all font-mono text-xs text-muted-foreground">{t('detail.pipelineViewer.poolGallery.seed', { seed: candidate.seed })}</p> : null}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Badge variant={candidate.primary ? 'default' : 'outline'} className="rounded-md">
                    {candidate.primary ? <Check className="size-3" /> : null}
                    {candidate.primary ? t('detail.pipelineViewer.poolGallery.primary') : `#${candidate.rank}`}
                  </Badge>
                  <span className="text-xs font-medium text-muted-foreground">
                    {t('detail.pipelineViewer.poolGallery.score', { score: Math.round(candidate.score) })}
                  </span>
                </div>
                {!candidate.primary ? (
                  <span className="inline-flex h-6 w-full items-center justify-center gap-1 rounded-md border bg-background px-2 text-xs font-medium">
                    {selecting ? <Loader2 className="size-3 animate-spin" /> : null}
                    {t('detail.pipelineViewer.poolGallery.useAsPrimary')}
                  </span>
                ) : null}
              </div>
            </button>
            {candidate.replayable && onReplay ? <Button type="button" variant="outline" size="sm" className="w-full" disabled={Boolean(replayingAssetId)} onClick={() => onReplay(candidate.asset.id)}>
              {replayingAssetId === candidate.asset.id ? <Loader2 className="size-3 animate-spin motion-reduce:animate-none" /> : null}
              {t('detail.pipelineViewer.poolGallery.replay')}
            </Button> : null}
            </div>
          )
        })}
      </div>
    </section>
  )
}

function parsePanelPoolCandidate(asset: Asset): PanelPoolCandidate | null {
  if (asset.asset_type !== 'panel_image' || asset.status !== 'passed') return null
  const payload = parsePayload(asset.payload)
  const metadata = payload?.metadata
  const groupId = typeof metadata?.pool_group_id === 'string' ? metadata.pool_group_id.trim() : ''
  if (!groupId) return null
  return {
    asset,
    groupId,
    score: numberValue(metadata?.pool_score),
    rank: numberValue(metadata?.pool_rank),
    primary: metadata?.pool_primary === true,
    style: typeof metadata?.pool_style_preset === 'string' ? metadata.pool_style_preset : '',
    seed: typeof payload?.seed === 'number' ? payload.seed : null,
    replayable: typeof payload?.replay_request?.Seed === 'number' && Boolean(payload.replay_request.RefImage),
  }
}

function panelIdForAsset(asset: Asset): string {
  return parsePayload(asset.payload)?.panel_id?.trim() ?? ''
}

function latestPoolGroup(candidates: PanelPoolCandidate[]): string {
  return [...candidates].sort((a, b) => b.asset.updated_at.localeCompare(a.asset.updated_at))[0]?.groupId ?? ''
}

function numberValue(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function parsePayload(payload: string): PanelPoolPayload | null {
  try {
    const parsed = JSON.parse(payload)
    return parsed && typeof parsed === 'object' ? (parsed as PanelPoolPayload) : null
  } catch {
    return null
  }
}

function writePoolPrimary(payload: string, primary: boolean): string {
  const parsed = parsePayload(payload) as (PanelPoolPayload & Record<string, unknown>) | null
  if (!parsed) return payload
  parsed.metadata = { ...(parsed.metadata ?? {}), pool_primary: primary }
  return JSON.stringify(parsed)
}
